import {useCallback, useRef, useState} from 'react';
import {ApiError} from './client';
import {
  uploadFileResumable,
  type ResumableEndpoint,
  type UploadProgress,
  type AttemptInfo,
} from './uploadResumable';

// Спільна черга аплоадів для всіх resumable-споживачів (дашборд, /test,
// кастоми, контент-реквести). До цього кожна сторінка мала майже-ідентичну
// копію цієї логіки (~150 рядків) — тепер усі вони звуться через цей хук.
//
// Що робить:
//   • тримає ліміт паралельних аплоадів (concurrency)
//   • запам'ятовує File + idempotencyKey щоб «Продолжить» після помилки
//     підняв той самий resumable-сесію (і тому продовжив з того байту, де
//     обірвалось — без перезаливу з 0)
//   • центрально обробляє abort/cancel і чистить items на done

export type UploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface UploadItem {
  id: string;
  filename: string;
  loaded: number;
  total: number;
  status: UploadStatus;
  // string | undefined (а не просто `error?: string`) — щоб під
  // exactOptionalPropertyTypes можна було явно скинути її в undefined
  // при «Продолжить», не вдаючись до destructuring-омайдану.
  error?: string | undefined;
  // Поточний номер спроби (1-based). >1 означає, що йде повтор. Прогрес при
  // ретраї НЕ скидаємо — нова спроба підхоплює offset з Drive і продовжує
  // з місця обриву. Верхньої межі немає — ретраїмо доки є прогрес або поки
  // не спрацює watchdog «тиша 30 хвилин».
  attempt: number;
  // Стан мережі на момент останнього attempt-callback-а. false → ми чекаємо
  // на повернення мережі, не робимо запитів.
  online: boolean;
  // Хронометраж для показу «скільки вже грузить» / «скільки тривало».
  // startedAt — момент, коли drain підхопив айтем у роботу (queued→uploading).
  // На retry скидаємо у undefined → наступний drain виставить заново, щоб
  // таймер відраховував поточну спробу, а не сумарний час включно з помилкою.
  // finishedAt — момент done/error. Юзаємо щоб «заморозити» фінальну тривалість
  // у картці поки вона ще видима.
  startedAt?: number | undefined;
  finishedAt?: number | undefined;
}

const REMOVE_DONE_AFTER_MS = 1500;
const MAX_CONCURRENT_UPLOADS = 4;

export interface ResumableQueueOptions {
  // null → черга на паузі (drain не ллє нічого). Юзаємо у дашборді поки
  // anketaId не вибрана. Інші підсистеми завжди передають готовий endpoint.
  endpoint: ResumableEndpoint | null;
  // Заголовки які додаємо у кожен HTTP-запит (POST/GET/PUT). Юзаємо для
  // Bearer-токена у кастомах/контенті.
  extraHeaders?: Record<string, string>;
  // Викликається після успішного аплоаду одного файла — для invalidate
  // TanStack Query кешу того, хто хоче бачити свіжий список.
  onAfterDone?: () => void;
}

export interface ResumableQueueApi {
  items: UploadItem[];
  enqueue: (files: File[]) => void;
  cancel: (id: string) => void;
  retry: (id: string) => void;
}

export function useResumableUploadQueue(opts: ResumableQueueOptions): ResumableQueueApi {
  const [items, setItems] = useState<UploadItem[]>([]);
  const pendingRef = useRef<Array<{id: string; file: File; idempotencyKey: string}>>([]);
  const activeRef = useRef(0);
  const controllersRef = useRef(new Map<string, AbortController>());
  // file + idempotencyKey тримаємо окремо від UploadItem (який іде в UI) —
  // юзер може тицьнути «Продолжить» після того як вичерпались автоматичні
  // ретраї, і тоді нам потрібен той самий key (для idempotent ensureSession,
  // який поверне існуючу Drive session) і той самий File. Запис тут переживає
  // помилку; чиститься на done / cancel / abort.
  const assetsRef = useRef(new Map<string, {file: File; idempotencyKey: string}>());

  // Тримаємо актуальний opts у ref — щоб drain (який створюється з фіксованими
  // захопленнями через useCallback) бачив свіжі endpoint/headers/onAfterDone
  // без перестворення (і відповідно без зайвих re-runs у споживачів).
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const drain = useCallback(() => {
    while (
      activeRef.current < MAX_CONCURRENT_UPLOADS &&
      pendingRef.current.length > 0
    ) {
      const currentEndpoint = optsRef.current.endpoint;
      if (currentEndpoint === null) {
        // Endpoint ще не готовий (e.g., у дашборді anketaId == null).
        // Айтеми лишаються у `queued`; drain їх підбере коли endpoint з'явиться.
        return;
      }
      const next = pendingRef.current.shift();
      if (!next) break;
      activeRef.current += 1;
      const {id, file, idempotencyKey} = next;
      const controller = new AbortController();
      controllersRef.current.set(id, controller);

      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? {...i, status: 'uploading', startedAt: i.startedAt ?? Date.now()}
            : i,
        ),
      );

      uploadFileResumable({
        file,
        endpoint: currentEndpoint,
        idempotencyKey,
        ...(optsRef.current.extraHeaders ? {extraHeaders: optsRef.current.extraHeaders} : {}),
        onProgress: ({loaded, total}: UploadProgress) => {
          setItems((prev) =>
            prev.map((i) => (i.id === id ? {...i, loaded, total} : i)),
          );
        },
        onAttempt: ({attempt, online}: AttemptInfo) => {
          setItems((prev) =>
            prev.map((i) => (i.id === id ? {...i, attempt, online} : i)),
          );
        },
        signal: controller.signal,
      })
        .then(() => {
          setItems((prev) =>
            prev.map((i) =>
              i.id === id
                ? {...i, status: 'done', loaded: i.total, finishedAt: Date.now()}
                : i,
            ),
          );
          optsRef.current.onAfterDone?.();
          setTimeout(() => {
            assetsRef.current.delete(id);
            setItems((prev) => prev.filter((i) => i.id !== id));
          }, REMOVE_DONE_AFTER_MS);
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') {
            assetsRef.current.delete(id);
            setItems((prev) => prev.filter((i) => i.id !== id));
            return;
          }
          const message =
            err instanceof ApiError
              ? err.code
              : err instanceof Error
                ? err.message
                : 'upload_failed';
          setItems((prev) =>
            prev.map((i) =>
              i.id === id
                ? {...i, status: 'error', error: message, finishedAt: Date.now()}
                : i,
            ),
          );
        })
        .finally(() => {
          activeRef.current -= 1;
          controllersRef.current.delete(id);
          drain();
        });
    }
  }, []);

  const enqueue = useCallback(
    (files: File[]) => {
      const fresh = files.map((file) => {
        // Idempotency-Key — стабільний на весь час життя цього UploadItem.
        // Перепідняття вкладки скидає queue, тому повторний enqueue буде
        // з новим ключем — і це правильно: користувач свідомо повторив дію.
        const idempotencyKey = crypto.randomUUID();
        return {
          item: {
            id: crypto.randomUUID(),
            filename: file.name,
            loaded: 0,
            total: file.size,
            status: 'queued' as const,
            attempt: 1,
            online: true,
          } satisfies UploadItem,
          file,
          idempotencyKey,
        };
      });
      setItems((prev) => [...fresh.map((f) => f.item), ...prev]);
      for (const {item, file, idempotencyKey} of fresh) {
        assetsRef.current.set(item.id, {file, idempotencyKey});
        pendingRef.current.push({id: item.id, file, idempotencyKey});
      }
      drain();
    },
    [drain],
  );

  const cancel = useCallback((id: string) => {
    const controller = controllersRef.current.get(id);
    if (controller) {
      controller.abort();
      return;
    }
    pendingRef.current = pendingRef.current.filter((q) => q.id !== id);
    assetsRef.current.delete(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  // Юзер тицьнув «Продолжить» на error-картці. Той самий idempotencyKey →
  // ensureSession поверне існуючу Drive session, fetchOffset спитає актуальний
  // offset, цикл продовжиться з місця обриву (або з більшого, якщо Drive
  // встиг прийняти більше ніж ми побачили).
  const retry = useCallback(
    (id: string) => {
      const assets = assetsRef.current.get(id);
      if (!assets) return;
      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? {
                ...i,
                status: 'queued',
                error: undefined,
                attempt: 1,
                online: true,
                startedAt: undefined,
                finishedAt: undefined,
              }
            : i,
        ),
      );
      pendingRef.current.push({id, file: assets.file, idempotencyKey: assets.idempotencyKey});
      drain();
    },
    [drain],
  );

  return {items, enqueue, cancel, retry};
}
